import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Ban, Check, CircleDashed, Loader2, Pause, X } from 'lucide-react';
import { cn } from '$lib/lib/cn';

// Run/portal/stage status taxonomy used across the admin dashboard.
// Mirrors the i18n keys under admin.status.* (DEV-217) so the pill is a
// single source of truth: feed it a status, get the right tone + icon + copy.
export type RunStatus =
  | 'pending'
  | 'running'
  | 'paused'
  | 'done'
  | 'done-with-errors'
  | 'failed'
  | 'cancelled';

interface RunStatusVisual {
  i18nKey: string;
  className: string;
  icon: (props: { size: number; className?: string }) => ReactNode;
  spin?: boolean;
}

const visuals: Record<RunStatus, RunStatusVisual> = {
  pending: {
    i18nKey: 'admin.status.pending',
    className: 'bg-surface-3 text-fg-muted border-rule-strong',
    icon: ({ size, className }) => <CircleDashed size={size} className={className} aria-hidden />
  },
  running: {
    i18nKey: 'admin.status.running',
    className: 'bg-info/12 text-info border-info/30',
    icon: ({ size, className }) => <Loader2 size={size} className={cn('motion-safe:animate-spin', className)} aria-hidden />,
    spin: true
  },
  paused: {
    i18nKey: 'admin.status.paused',
    className: 'bg-warning/15 text-warning border-warning/35',
    icon: ({ size, className }) => <Pause size={size} className={className} aria-hidden />
  },
  done: {
    i18nKey: 'admin.status.completed',
    className: 'bg-positive/12 text-positive border-positive/30',
    icon: ({ size, className }) => <Check size={size} className={className} aria-hidden />
  },
  'done-with-errors': {
    i18nKey: 'admin.status.completedWithErrors',
    className: 'bg-warning/15 text-warning border-warning/35',
    icon: ({ size, className }) => <AlertTriangle size={size} className={className} aria-hidden />
  },
  failed: {
    i18nKey: 'admin.status.failed',
    className: 'bg-danger/12 text-danger border-danger/30',
    icon: ({ size, className }) => <X size={size} className={className} aria-hidden />
  },
  cancelled: {
    i18nKey: 'admin.status.cancelled',
    className: 'bg-surface-3 text-fg-muted border-rule-strong',
    icon: ({ size, className }) => <Ban size={size} className={className} aria-hidden />
  }
};

export interface StatusPillProps {
  status: RunStatus;
  // Override the rendered label. Defaults to the i18n string for the status.
  label?: ReactNode;
  // For done-with-errors, surface the error count via i18n interpolation.
  errorCount?: number;
  className?: string;
  'data-testid'?: string;
}

// Subtle pulsing dot used inline by StatusPill when status === 'running' so
// the running state reads as live without an icon-only pill.
export function StatusPill({
  status,
  label,
  errorCount,
  className,
  'data-testid': testId
}: StatusPillProps) {
  const { t } = useTranslation();
  const visual = visuals[status];

  let resolvedLabel: ReactNode = label;
  if (resolvedLabel == null) {
    if (status === 'done-with-errors' && typeof errorCount === 'number' && errorCount > 0) {
      resolvedLabel = t('admin.status.completedWithErrorsCount', { count: errorCount });
    } else {
      resolvedLabel = t(visual.i18nKey);
    }
  }

  return (
    <span
      data-testid={testId}
      data-status={status}
      className={cn(
        'inline-flex h-5 items-center gap-1 rounded-xs border px-1.5 text-2xs font-medium',
        visual.className,
        className
      )}
    >
      {visual.icon({ size: 11 })}
      <span>{resolvedLabel}</span>
    </span>
  );
}
